import { ChangeEvent, FC, useEffect } from "react";
import { UseFormReturn, useFieldArray } from "react-hook-form";
import {
  MiButton,
  MiDetailFields,
  MiPlaceholderList,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  INPUT_TYPES,
} from "@miplatform/constants";
import { ITransferListItem } from "@miplatform/types";
import { mdiMinus, mdiPlus } from "@mdi/js";
import { TransferList } from "@mibuildweb/components";
import { Box, Typography, useTheme } from "@mui/material";
import { ConfigAccordionHeader } from "../../../../../Config/components/ConfigAccordionHeader/ConfigAccordionHeader";
import { IAttributeData, ICategories } from "../../Attributes.types";
import { useAttributeDetailsTabViewModel } from "./useAttributeDetailsTabViewModel";

const CHARACTER_TYPES = {
  ALPHANUMERIC: "Alphanumeric",
  NUMERIC: "Numeric",
};

export const AttributeDetailsTab: FC<AttributeDetailsTabProps> = ({ form }) => {
  const {
    control,
    setValue,
    clearErrors,
    trigger,
    watch,
    formState: { isValid },
  } = form;
  const { fields, append, remove } = useFieldArray({
    control,
    name: "attributeOptions",
  });

  const theme = useTheme();
  const { categories, fetchCategories } = useAttributeDetailsTabViewModel();

  const {
    attributeOptions,
    id: attributeId,
    inputCharacterType,
    allowManualEntry,
    categoryIds = [],
  } = watch();

  const attachedCategories = categoryIds as ICategories[];

  useEffect(() => {
    fetchCategories();
  }, []);

  const areOptionNamesInvalid = attributeOptions.some(
    (option) => option.value === ""
  );
  const isFirstField = attributeOptions.length === 1;

  const attributeDetailFields = [
    {
      label: "Attribute Name",
      name: "name",
      formControl: control,
      InputProps: { inputProps: { maxLength: 32 } },
      required: true,
    },
    {
      fieldType: INPUT_TYPES.SWITCH,
      label: (
        <Box display="flex" gap={1}>
          <Typography fontWeight={theme.fontWeight.semiBold}>
            Allow Manual Entry:
          </Typography>
          <Typography>
            Let users type in their own values when creating an item. Keep off
            to restrict them to the options you define.
          </Typography>
        </Box>
      ),
      setValue: (e: ChangeEvent<HTMLInputElement>) => {
        const isChecked = e.target.checked;
        setValue("allowManualEntry", isChecked, {
          shouldValidate: true,
          shouldDirty: true,
        });
        if (!isChecked || inputCharacterType === CHARACTER_TYPES.NUMERIC) {
          trigger("attributeOptions");
        } else {
          clearErrors("attributeOptions");
        }
      },
      labelPlacement: "end",
      name: "allowManualEntry",
      formControl: control,
      width: "100%",
    } as any,
  ];

  const validationTypeField = [
    {
      fieldType: INPUT_TYPES.RADIO_GROUP,
      name: "inputCharacterType",
      formControl: control,
      options: [
        {
          label: CHARACTER_TYPES.ALPHANUMERIC,
          value: CHARACTER_TYPES.ALPHANUMERIC,
        },
        {
          label: CHARACTER_TYPES.NUMERIC,
          value: CHARACTER_TYPES.NUMERIC,
        },
      ],
      setValue: (e: ChangeEvent<HTMLInputElement>) => {
        const selectedValue = e.target.value as "Alphanumeric" | "Numeric";
        setValue("inputCharacterType", selectedValue, {
          shouldValidate: true,
          shouldDirty: true,
        });
        if (selectedValue === CHARACTER_TYPES.NUMERIC) {
          trigger("attributeOptions");
        } else {
          clearErrors("attributeOptions");
        }
      },
    } as any,
  ];

  const optionField = (index: number) => [
    {
      name: `attributeOptions.${index}.value`,
      InputProps: { inputProps: { maxLength: 32 } },
      formControl: control,
      label: "Option Name",
      required: !allowManualEntry,
    },
    {
      name: `attributeOptions.${index}.abbreviation`,
      InputProps: { inputProps: { maxLength: 3 } },
      formControl: control,
      label: "Abbreviation",
      required: !allowManualEntry,
      setValue: (e: ChangeEvent<HTMLInputElement>) => {
        const text = e.target.value.toUpperCase();
        setValue(`attributeOptions.${index}.abbreviation`, text, {
          shouldDirty: true,
          shouldValidate: true,
        });
      },
    },
    {
      fieldType: INPUT_TYPES.CUSTOM,
      formControl: control,
      minWidth: 0,
      flexGrow: 0,
      width: "fit-content",
      customComponent: (
        <MiButton
          onClick={() => remove(index)}
          disabled={!attributeId && isFirstField}
          variant={BUTTON_VARIANTS.CONTAINED}
          data-testid="delete-field-btn"
          color={BUTTON_COLORS.RED}
          startIcon={{ path: mdiMinus }}
          sx={{ borderRadius: "50%", padding: theme.spacing(0) }}
        />
      ),
    } as any,
  ];

  const availableCategories: ITransferListItem[] = categories.map((c) => ({
    ...c,
    key: c.id,
    title: c.name,
  }));

  const selectedCategories: ITransferListItem[] = attachedCategories
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((c) => ({
      ...c,
      key: c.id,
      title: c.name,
    }));

  const handleAddCategory = (key: string) => {
    const selectedCat = categories.find((c) => c.id === key);
    if (!selectedCat) {
      return;
    }
    const updatedCategories = [...attachedCategories, selectedCat];
    setValue("categoryIds", updatedCategories, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const handleRemoveCategory = (key: string) => {
    const updatedCat = attachedCategories.filter((c) => c.id !== key);
    setValue("categoryIds", updatedCat, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  return (
    <>
      <ConfigAccordionHeader title="Enter Attribute Details">
        <MiDetailFields
          detailFields={attributeDetailFields}
          headerProps={{
            subtitle:
              "Name the attribute clearly. Enable Allow Manual Entry if users should type custom values; leave it off to restrict the users to preset options.",
            variant: "text",
          }}
        />
      </ConfigAccordionHeader>
      <ConfigAccordionHeader title="Enter Options">
        <MiDetailFields
          detailFields={validationTypeField}
          headerProps={{
            subtitle: "Decide what kind of values this attribute accepts.",
            variant: "text",
            style: { paddingBottom: 0 },
          }}
        />
        {fields.map((option, index) => (
          <MiDetailFields
            key={option.id}
            detailFields={optionField(index)}
            customWrapperStyle={{ display: "flex", alignItems: "center" }}
          />
        ))}
        {attributeOptions.length === 0 && (
          <MiPlaceholderList
            text="No options added."
            helperText="To add, begin editing and click + Add Option button."
          />
        )}
        <MiButton
          variant={BUTTON_VARIANTS.OUTLINED}
          startIcon={{ path: mdiPlus }}
          onClick={() => {
            const newOption = { value: "", abbreviation: "" };
            append(newOption);
          }}
          sx={{ margin: 1 }}
          disabled={allowManualEntry ? areOptionNamesInvalid : !isValid}
          data-testid="add-field-btn"
          title="Add Option"
        />
      </ConfigAccordionHeader>
      <ConfigAccordionHeader title="Attach Categories">
        <Box sx={{ height: 400, overflow: "auto" }}>
          <TransferList
            availableItems={availableCategories}
            selectedItems={selectedCategories}
            onAddItem={handleAddCategory}
            onRemoveItem={handleRemoveCategory}
            availableTitle="Available Categories"
            selectedTitle="Selected Categories"
            showAvailableItemsSearch
          />
        </Box>
      </ConfigAccordionHeader>
    </>
  );
};

interface AttributeDetailsTabProps {
  form: UseFormReturn<IAttributeData>;
}
